import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, Eye, EyeOff,
  Plus, X, RefreshCw, Menu, Info, Phone, User, Repeat, MapPin, Search,
  Settings, Check, ChevronDown, CheckSquare, Square, Trash2, CalendarDays
} from 'lucide-react';
import Modal from './Modal';
import SearchableSelect from './SearchableSelect';
import { safeFetch } from '../../lib/api';
import { confirmDialog } from '../../lib/confirmStore';

// --- Types & Interfaces ---
export interface CalendarCategory {
  id: string;
  name: string;
  color: string; // Tailwind hex color
  textColor: string;
  visible: boolean;
}

export interface SchedulingEvent {
  id: string;
  title: string;
  startTime: string; // ISO String (e.g., 2025-05-12T10:30:00)
  endTime: string;   // ISO String (e.g., 2025-05-12T11:30:00)
  allDay?: boolean;
  calendarId: string; // matches Category ID (a real event type)
  status?: string;    // Scheduled / Pending / Awaiting Response / Confirmed / ...
  assignedTo?: string;
  contactInfo?: string; // e.g. Phone number
  recurrenceRule?: string; // e.g., 'Weekly', 'Daily'
  location?: string;
  description?: string;
  raw?: any;
}

interface AgentOption {
  uid: string;
  name: string;
  email: string;
  role?: string;
}

/** Real workspace event types (mirrors the main Events tab + useEvents). */
const EVENT_TYPE_IDS = ['embassy', 'interview', 'orientation', 'training', 'flight', 'medical', 'other'];
const STATUS_OPTIONS = ['Scheduled', 'Pending', 'Awaiting Response', 'Confirmed', 'Rescheduled', 'Completed', 'Cancelled', 'Declined'];

/** Map a legacy/demo calendar id (or an unknown type) to a real event type. */
function normalizeCalendarId(id?: string): string {
  const legacy: Record<string, string> = {
    'tours-classes': 'interview',
    'nurses-oncall': 'embassy',
    'nurses-open': 'orientation',
    'cleaning-techs': 'training',
    'vacation-pto': 'other',
    'room-bookings': 'other',
    'patients-edd': 'other',
  };
  if (!id) return 'other';
  const lower = id.toLowerCase();
  if (EVENT_TYPE_IDS.includes(lower)) return lower;
  if (legacy[lower]) return legacy[lower];
  return 'other';
}

interface SchedulingCalendarProps {
  initialEvents?: SchedulingEvent[];
  onEventChange?: (events: SchedulingEvent[]) => void;
  onSelectEvent?: (event: SchedulingEvent) => void;
  userRole?: 'admin' | 'referrer' | 'employer' | 'sponsor' | 'applicant';
  title?: string;
  agentsList?: { uid: string; name: string; email: string; role?: string }[];
}

// --- Calendar categories = the REAL workspace event types ---
const DEFAULT_CATEGORIES: CalendarCategory[] = [
  { id: 'embassy', name: 'Embassy', color: '#3B82F6', textColor: '#FFFFFF', visible: true },
  { id: 'interview', name: 'Interview', color: '#10B981', textColor: '#FFFFFF', visible: true },
  { id: 'orientation', name: 'Orientation', color: '#8B5CF6', textColor: '#FFFFFF', visible: true },
  { id: 'training', name: 'Training', color: '#F59E0B', textColor: '#FFFFFF', visible: true },
  { id: 'flight', name: 'Flight', color: '#EC4899', textColor: '#FFFFFF', visible: true },
  { id: 'medical', name: 'Medical', color: '#EF4444', textColor: '#FFFFFF', visible: true },
  { id: 'other', name: 'Other', color: '#6B7280', textColor: '#FFFFFF', visible: true },
];

export default function SchedulingCalendar({
  initialEvents,
  onEventChange,
  onSelectEvent,
  userRole = 'admin',
  title = 'Global Scheduling Platform',
  agentsList = []
}: SchedulingCalendarProps) {
  // --- States ---
  const [events, setEvents] = useState<SchedulingEvent[]>(() =>
    (initialEvents || []).map(ev => ({ ...ev, calendarId: normalizeCalendarId(ev.calendarId) }))
  );

  useEffect(() => {
    if (initialEvents && initialEvents.length > 0) {
      setEvents(initialEvents.map(ev => ({ ...ev, calendarId: normalizeCalendarId(ev.calendarId) })));
    }
  }, [initialEvents]);

  const [categories, setCategories] = useState<CalendarCategory[]>(DEFAULT_CATEGORIES);
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [view, setView] = useState<'Scheduler' | 'Day' | 'Week' | '3 Weeks' | 'Month' | 'Year' | 'Timeline' | 'Table' | 'Agenda' | 'List' | 'Tiles'>('Week');

  // Filtering & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  // Real workspace agents for the "Assigned To" selector. Prefers the
  // agentsList prop when provided; otherwise fetches from the workspace-scoped
  // /api/admin/agents endpoint. NEVER falls back to made-up names.
  const [fetchedAgents, setFetchedAgents] = useState<AgentOption[]>([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [agentsError, setAgentsError] = useState(false);
  const agents: AgentOption[] = (agentsList && agentsList.length > 0) ? agentsList : fetchedAgents;

  // Capability model (Batch R2): this component's edit affordances have
  // always keyed on userRole — the /api/admin/agents fetch below and the
  // event popup's Delete + "Reschedule & Edit" actions both run only for
  // 'admin'. Creation belongs to the same capability: grid-cell click
  // creation is gated on the same predicate, so a surface that cannot
  // edit events (employer, referrer, sponsor, applicant) cannot open the
  // create form either — previously a cell click opened it for every
  // role and the "save" only mutated the host's local state (or nothing
  // at all), a false promise. Admin surfaces (EventsManager grid,
  // MyEventsView) are unchanged.
  const canEditEvents = userRole === 'admin';

  useEffect(() => {
    if (agentsList && agentsList.length > 0) { setAgentsLoaded(true); return; }
    if (userRole !== 'admin') { setAgentsLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await safeFetch('/api/admin/agents');
        if (!cancelled) {
          setFetchedAgents(Array.isArray(data.agents) ? data.agents : []);
          setAgentsLoaded(true);
        }
      } catch (err) {
        console.warn('Failed to load workspace agents:', err);
        if (!cancelled) { setAgentsError(true); setAgentsLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [userRole, agentsList]);

  // Collapse sections
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [aboutCollapsed, setAboutCollapsed] = useState(false);

  // Modal / Detail views
  const [selectedEvent, setSelectedEvent] = useState<SchedulingEvent | null>(null);
  const [editingEvent, setEditingEvent] = useState<SchedulingEvent | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Popup & Day popup modal states for days with multiple events
  const [datePopupEvents, setDatePopupEvents] = useState<SchedulingEvent[]>([]);
  const [datePopupDate, setDatePopupDate] = useState<Date | null>(null);

  // Drag creation hours helper
  const [dragStartSlot, setDragStartSlot] = useState<{ day: Date; hour: number } | null>(null);

  // Refresh animation state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // --- Dynamic Time Slot Configurations ---
  const startHour = 7; // 7am
  const endHour = 21;  // 9pm (21:00)
  const hourHeight = 68; // height of an hour slot in pixels

  // Trigger state callback
  const updateEvents = (newEvents: SchedulingEvent[]) => {
    setEvents(newEvents);
    if (onEventChange) {
      onEventChange(newEvents);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };

  // --- Date Range Calculations ---
  // Start of the week (Sunday)
  const startOfWeekDate = useMemo(() => {
    const d = new Date(currentDate);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentDate]);

  // End of the week (Saturday)
  const endOfWeekDate = useMemo(() => {
    const d = new Date(startOfWeekDate);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [startOfWeekDate]);

  // Current Week dates
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeekDate);
      d.setDate(startOfWeekDate.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [startOfWeekDate]);

  // Date Range Label text
  const dateRangeLabel = useMemo(() => {
    if (view === 'Day') {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (view === 'Week' || view === 'Scheduler') {
      const startStr = startOfWeekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endStr = endOfWeekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startStr} – ${endStr}`;
    }
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [currentDate, view, startOfWeekDate, endOfWeekDate]);

  // --- Visibility Filter logic ---
  const visibleCategoryIds = useMemo(() => {
    return categories.filter(c => c.visible).map(c => c.id);
  }, [categories]);

  const filteredEvents = useMemo(() => {
    return events.filter(ev => {
      // Category visibility
      if (!visibleCategoryIds.includes(ev.calendarId)) return false;
      // Search query — matches the main Events tab fields: title, type,
      // location, assignee, description.
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = ev.title.toLowerCase().includes(q);
        const matchType = (ev.calendarId || '').toLowerCase().includes(q);
        const matchAssigned = (ev.assignedTo || '').toLowerCase().includes(q);
        const matchLoc = (ev.location || '').toLowerCase().includes(q);
        const matchDesc = (ev.description || '').toLowerCase().includes(q);
        if (!matchTitle && !matchType && !matchAssigned && !matchLoc && !matchDesc) return false;
      }
      // Event type dropdown filter
      if (filterCategory !== 'All' && ev.calendarId !== filterCategory) return false;
      // Status filter (matches the main Events tab)
      if (filterStatus !== 'All' && (ev.status || '') !== filterStatus) return false;
      return true;
    });
  }, [events, visibleCategoryIds, searchQuery, filterCategory, filterStatus]);

  // --- Layout Separator Lists ---
  // All-day multi-day bars
  const allDayEvents = useMemo(() => {
    return filteredEvents.filter(ev => ev.allDay);
  }, [filteredEvents]);

  // Hourly grid events
  const timedEvents = useMemo(() => {
    return filteredEvents.filter(ev => !ev.allDay);
  }, [filteredEvents]);

  // --- Category helpers ---
  const toggleCategoryVisibility = (id: string) => {
    setCategories(prev => prev.map(c => c.id === id ? { ...c, visible: !c.visible } : c));
  };

  const toggleAllCategories = (visible: boolean) => {
    setCategories(prev => prev.map(c => ({ ...c, visible })));
  };

  // --- Grid coordinate calculations ---
  const timeToMinutes = (timeStr: string) => {
    const parts = timeStr.split('T')[1]?.slice(0, 5).split(':');
    if (!parts) return 0;
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  };

  // Compute layout specs for timed events
  const layoutEventsForDay = (day: Date) => {
    const targetDateStr = day.toISOString().split('T')[0];
    const dayEvents = timedEvents.filter(ev => ev.startTime.startsWith(targetDateStr));

    // Sort by startTime
    dayEvents.sort((a, b) => {
      const aStart = timeToMinutes(a.startTime);
      const bStart = timeToMinutes(b.startTime);
      if (aStart !== bStart) return aStart - bStart;
      return timeToMinutes(a.endTime) - timeToMinutes(b.endTime);
    });

    // Detect overlapping columns
    const columns: SchedulingEvent[][] = [];
    dayEvents.forEach(ev => {
      let placed = false;
      const evStart = timeToMinutes(ev.startTime);
      const evEnd = timeToMinutes(ev.endTime);

      for (let i = 0; i < columns.length; i++) {
        const lastInCol = columns[i][columns[i].length - 1];
        const lastEnd = timeToMinutes(lastInCol.endTime);
        if (evStart >= lastEnd) {
          columns[i].push(ev);
          placed = true;
          break;
        }
      }

      if (!placed) {
        columns.push([ev]);
      }
    });

    const results: { event: SchedulingEvent; left: number; width: number }[] = [];
    columns.forEach((colEvents, colIdx) => {
      colEvents.forEach(ev => {
        results.push({
          event: ev,
          left: (colIdx / columns.length) * 100,
          width: 100 / columns.length
        });
      });
    });

    return results;
  };

  // --- Create/Edit/Delete Actions ---
  const handleSaveEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent) return;

    if (isCreating) {
      updateEvents([...events, editingEvent]);
    } else {
      updateEvents(events.map(ev => ev.id === editingEvent.id ? editingEvent : ev));
    }
    setEditingEvent(null);
    setIsCreating(false);
  };

  const handleDeleteEvent = async (id: string) => {
    if (await confirmDialog('Are you sure you want to delete this event?')) {
      updateEvents(events.filter(ev => ev.id !== id));
      setSelectedEvent(null);
    }
  };

  const handleGridCellClick = (day: Date, hour: number) => {
    if (!canEditEvents) return;
    const formattedHour = String(hour).padStart(2, '0');
    const startStr = `${day.toISOString().split('T')[0]}T${formattedHour}:00:00`;
    const endStr = `${day.toISOString().split('T')[0]}T${String(hour + 1).padStart(2, '0')}:00:00`;

    setIsCreating(true);
    setEditingEvent({
      id: String(Date.now()),
      title: 'New Schedule Event',
      startTime: startStr,
      endTime: endStr,
      calendarId: categories[0].id,
      assignedTo: '',
      contactInfo: '',
      location: '',
      description: '',
    });
  };

  // Mini picker calculation helpers
  const miniPickerMonthDays = useMemo(() => {
    const yr = currentDate.getFullYear();
    const mo = currentDate.getMonth();
    const first = new Date(yr, mo, 1);
    const last = new Date(yr, mo + 1, 0);
    const offset = first.getDay();
    const days: (Date | null)[] = [];

    // prior month padded
    for (let i = 0; i < offset; i++) {
      days.push(null);
    }

    // active days
    for (let d = 1; d <= last.getDate(); d++) {
      days.push(new Date(yr, mo, d));
    }
    return days;
  }, [currentDate]);

  return (
    <div className="flex flex-col lg:flex-row gap-6 text-slate-800 font-sans min-h-[750px] w-full bg-[#FAFAFC] p-4 lg:p-6 rounded-[24px] border border-slate-200/80 shadow-lg">

      {/* 1. LEFT SIDEBAR */}
      <div className={`w-full lg:w-[340px] shrink-0 space-y-6 ${sidebarCollapsed ? 'hidden lg:block lg:w-16' : ''} transition-all duration-300`}>
        {sidebarCollapsed ? (
          <button onClick={() => setSidebarCollapsed(false)} className="p-3 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 shadow-sm w-full flex items-center justify-center">
            <Menu className="h-5 w-5 text-slate-500" />
          </button>
        ) : (
          <>
            {/* MINI MONTH-PICKER CALENDAR */}
            <div className="bg-white border border-slate-200 rounded-[20px] p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-2">
                <span className="text-xs font-extrabold text-slate-800">
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                    className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition cursor-pointer"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                    className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition cursor-pointer"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Grid of Mini Picker */}
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 mb-1">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                  <div key={idx}>{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {miniPickerMonthDays.map((date, idx) => {
                  if (!date) return <div key={idx} />;
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = date.toDateString() === currentDate.toDateString();
                  return (
                    <button
                      key={idx}
                      onClick={() => setCurrentDate(date)}
                      className={`h-6 text-[10px] rounded-full flex items-center justify-center font-bold transition-all cursor-pointer ${
                        isSelected ? 'bg-brand text-white' : isToday ? 'border border-brand text-brand' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* COLLAPSIBLE ABOUT / INSTRUCTION SECTION */}
            <div className="bg-white border border-slate-200 rounded-[20px] p-4 shadow-sm">
              <button
                onClick={() => setAboutCollapsed(!aboutCollapsed)}
                className="w-full flex items-center justify-between text-xs font-extrabold text-slate-800 border-b border-slate-100 pb-2 cursor-pointer"
              >
                <span className="flex items-center gap-1.5"><Info className="h-4 w-4 text-brand" /> Scheduler Guide</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${aboutCollapsed ? '-rotate-90' : ''}`} />
              </button>

              {!aboutCollapsed && (
                <div className="pt-3 text-[11px] text-slate-500 leading-relaxed space-y-2 animate-fade-in">
                  <p>🗓️ <strong>Interactive Grid:</strong> Click on any empty hourly slot to register a new appointment on that day.</p>
                  <p>💡 <strong>Layers:</strong> Toggle eye or checkbox to instantly filter categories of events shown.</p>
                  <p>👥 <strong>SaaS Standard:</strong> Premium drag and click layout optimized for administrative teams.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 2. MAIN CALENDAR GRID PANEL */}
      <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-[24px] shadow-sm overflow-hidden min-w-0">

        {/* TOP TOOLBAR */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 p-4 border-b border-slate-200 bg-slate-50/50">
          <div className="flex flex-wrap items-center gap-2">
            {/* Hamburger menu */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 hover:bg-slate-200/60 rounded-lg text-slate-600 transition cursor-pointer"
              title="Toggle Left Sidebar"
            >
              <Menu className="h-4 w-4" />
            </button>

            {/* View Selector Tabs */}
            <div className="flex items-center bg-slate-200/60 rounded-xl p-0.5 overflow-x-auto max-w-full">
              {(['Scheduler', 'Day', 'Week', 'Month'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                    view === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>

            {/* Today Button */}
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700 transition cursor-pointer"
            >
              Today
            </button>
          </div>

          <div className="flex items-center justify-between xl:justify-end gap-3">
            {/* Navigation Arrows & Current Label */}
            <div className="flex items-center bg-white border border-slate-200 rounded-xl p-0.5">
              <button
                onClick={() => {
                  const d = new Date(currentDate);
                  if (view === 'Day') d.setDate(d.getDate() - 1);
                  else d.setDate(d.getDate() - 7);
                  setCurrentDate(d);
                }}
                className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs font-extrabold text-slate-800 px-3 py-1 flex items-center gap-1">
                {dateRangeLabel}
                <ChevronDown className="h-3 w-3 text-slate-400" />
              </span>
              <button
                onClick={() => {
                  const d = new Date(currentDate);
                  if (view === 'Day') d.setDate(d.getDate() + 1);
                  else d.setDate(d.getDate() + 7);
                  setCurrentDate(d);
                }}
                className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition cursor-pointer"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Refresh Icon */}
            <button
              onClick={handleRefresh}
              className={`p-2 hover:bg-slate-100 rounded-xl text-slate-500 transition cursor-pointer ${isRefreshing ? 'animate-spin text-brand' : ''}`}
              title="Refresh Calendar"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* SEARCH + FILTER TOOLBAR (mirrors the main Events tab) */}
        <div className="flex flex-col md:flex-row gap-2.5 md:items-center px-1 pb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search events, type, people, location..."
              className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-brand font-medium text-slate-700"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-brand font-bold text-slate-700 cursor-pointer"
            >
              <option value="All">All Event Types</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-brand font-bold text-slate-700 cursor-pointer"
            >
              <option value="All">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(searchQuery || filterCategory !== 'All' || filterStatus !== 'All') && (
              <button
                onClick={() => { setSearchQuery(''); setFilterCategory('All'); setFilterStatus('All'); }}
                className="px-2.5 py-2 text-xs font-bold text-rose-500 hover:bg-rose-50 rounded-xl transition cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* DATE HEADER ROW */}
        {view === 'Month' ? (
          <div className="p-4 grid grid-cols-7 gap-px bg-slate-200 text-center text-xs font-bold text-slate-500">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="bg-slate-50 py-2 uppercase tracking-wider">{d}</div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-8 border-b border-slate-200 bg-slate-50 select-none">
            {/* Empty space corner */}
            <div className="border-r border-slate-200 p-2 flex items-center justify-center text-[10px] font-bold text-slate-400">
              GMT
            </div>

            {/* 7 Columns or Day Columns */}
            {view === 'Day' ? (
              <div className="col-span-7 grid grid-cols-1">
                {(() => {
                  const isToday = currentDate.toDateString() === new Date().toDateString();
                  return (
                    <div className={`p-2.5 text-center border-r border-slate-200 last:border-r-0 ${isToday ? 'bg-blue-50/40' : ''}`}>
                      <span className="text-xs font-bold text-slate-400 uppercase">{currentDate.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                      <div className={`text-base font-black mt-0.5 h-7 w-7 mx-auto flex items-center justify-center rounded-full ${isToday ? 'bg-brand text-white shadow-sm' : 'text-slate-800'}`}>
                        {currentDate.getDate()}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="col-span-7 grid grid-cols-7">
                {weekDates.map((d, idx) => {
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <div key={idx} className={`p-2.5 text-center border-r border-slate-200 last:border-r-0 ${isToday ? 'bg-blue-50/40' : ''}`}>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                      <div className={`text-sm font-black mt-0.5 h-6 w-6 mx-auto flex items-center justify-center rounded-full ${isToday ? 'bg-brand text-white shadow-sm' : 'text-slate-700'}`}>
                        {d.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ALL-DAY / MULTI-DAY EVENT BAND */}
        {view !== 'Month' && allDayEvents.length > 0 && (
          <div className="grid grid-cols-8 border-b border-slate-200 bg-amber-50/20 p-2 gap-px select-none">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600/80 flex items-center justify-center">
              All-Day
            </div>
            <div className="col-span-7 space-y-1">
              {allDayEvents.map(ev => {
                const cat = categories.find(c => c.id === ev.calendarId);
                return (
                  <div
                    key={ev.id}
                    onClick={() => { if (onSelectEvent) onSelectEvent(ev); else setSelectedEvent(ev); }}
                    className="px-3 py-1 rounded-lg text-[11px] font-bold text-white shadow-sm cursor-pointer hover:scale-[1.01] transition-transform flex items-center justify-between"
                    style={{ backgroundColor: cat?.color || '#3B82F6' }}
                  >
                    <span>✈️ {ev.title} {ev.assignedTo ? `(${ev.assignedTo})` : ''}</span>
                    {ev.contactInfo && <span className="text-[11px] opacity-90">{ev.contactInfo}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* MAIN GRID TIME-SLOT CONTAINER */}
        <div className="flex-1 overflow-y-auto max-h-[580px] relative select-none">
          {view === 'Month' ? (
            /* Month Calendar Grid cells mapping */
            <div className="grid grid-cols-7 gap-px bg-slate-200">
              {(() => {
                const days = [];
                const currentYear = currentDate.getFullYear();
                const currentMonth = currentDate.getMonth();

                const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();

                const prevMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
                const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
                const totalDaysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

                // Pad previous month days
                for (let i = firstDayIndex - 1; i >= 0; i--) {
                  days.push({
                    day: totalDaysInPrevMonth - i,
                    monthOffset: -1,
                    date: new Date(prevMonthYear, prevMonth, totalDaysInPrevMonth - i)
                  });
                }

                // Current month days
                for (let day = 1; day <= totalDaysInMonth; day++) {
                  days.push({
                    day,
                    monthOffset: 0,
                    date: new Date(currentYear, currentMonth, day)
                  });
                }

                // Next month gray days to complete 42 elements
                const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;
                const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
                const remaining = 42 - days.length;
                for (let day = 1; day <= remaining; day++) {
                  days.push({
                    day,
                    monthOffset: 1,
                    date: new Date(nextMonthYear, nextMonth, day)
                  });
                }

                return days.map(({ day, monthOffset, date }, idx) => {
                  const today = new Date();
                  const isToday = day === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
                  const isSameMonth = monthOffset === 0;

                  const dayEvents = filteredEvents.filter(ev => {
                    const d = new Date(ev.startTime);
                    return d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
                  });

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (dayEvents.length > 1) {
                          setDatePopupEvents(dayEvents);
                          setDatePopupDate(date);
                        } else if (dayEvents.length === 1) {
                          if (onSelectEvent) onSelectEvent(dayEvents[0]);
                          else setSelectedEvent(dayEvents[0]);
                        }
                      }}
                      className={`min-h-[105px] p-2 bg-white flex flex-col justify-between border-b border-r border-slate-100 transition-all ${
                        dayEvents.length > 0 ? 'hover:bg-slate-50/50 cursor-pointer' : 'cursor-default'
                      } ${
                        isToday ? 'bg-brand/5 ring-1 ring-brand/30 ring-inset' : ''
                      } ${!isSameMonth ? 'bg-slate-50/40 text-slate-400' : 'text-slate-800'}`}
                    >
                      <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                        <span className={`text-[11px] font-black h-5 w-5 flex items-center justify-center rounded-full ${
                          isToday ? 'bg-brand text-white font-mono shadow' : !isSameMonth ? 'text-slate-400' : 'text-slate-700 bg-slate-200'
                        }`}>
                          {day}
                        </span>
                        {dayEvents.length > 0 && (
                          <span className="text-[11px] bg-slate-200 px-1.5 py-0.2 rounded font-bold text-slate-700">
                            {dayEvents.length}
                          </span>
                        )}
                      </div>

                      <div className="flex-1 space-y-1 overflow-y-auto max-h-[70px] mt-1.5">
                        {dayEvents.slice(0, 3).map(ev => {
                          const cat = categories.find(c => c.id === ev.calendarId);
                          return (
                            <div
                              key={ev.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (dayEvents.length > 1) {
                                  setDatePopupEvents(dayEvents);
                                  setDatePopupDate(date);
                                } else {
                                  if (onSelectEvent) onSelectEvent(ev);
                                  else setSelectedEvent(ev);
                                }
                              }}
                              className="px-1.5 py-0.5 rounded text-[11px] font-bold truncate leading-tight shadow-sm select-none cursor-pointer text-white hover:opacity-90 border border-black/5"
                              style={{ backgroundColor: cat?.color || '#3B82F6' }}
                              title={ev.title}
                            >
                              {ev.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <p
                            onClick={(e) => {
                              e.stopPropagation();
                              setDatePopupEvents(dayEvents);
                              setDatePopupDate(date);
                            }}
                            className="text-[11px] text-brand font-black text-center hover:underline cursor-pointer"
                          >
                            +{dayEvents.length - 3} more
                          </p>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            /* Timed hourly grid layout (Week & Day Views) */
            <div className="grid grid-cols-8 relative select-none">

              {/* Left Column Hourly Labels (e.g. 7am, 8am) */}
              <div className="border-r border-slate-200">
                {Array.from({ length: endHour - startHour + 1 }).map((_, i) => {
                  const hr = startHour + i;
                  const label = hr === 12 ? '12 PM' : hr > 12 ? `${hr - 12} PM` : `${hr} AM`;
                  return (
                    <div
                      key={hr}
                      className="text-[10px] font-extrabold text-slate-500 border-b border-slate-100 flex items-start justify-center pr-2 pt-2 text-right uppercase"
                      style={{ height: `${hourHeight}px` }}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>

              {/* Day time-grid columns */}
              <div className="col-span-7 grid grid-cols-1 xl:grid-cols-7 relative">
                {/* Horizontal time lines helper */}
                <div className="absolute inset-0 pointer-events-none flex flex-col">
                  {Array.from({ length: endHour - startHour + 1 }).map((_, i) => (
                    <div
                      key={i}
                      className="border-b border-slate-100 w-full"
                      style={{ height: `${hourHeight}px` }}
                    />
                  ))}
                </div>

                {/* Day column containers */}
                {view === 'Day' ? (
                  /* Single Day Column */
                  <div className="relative border-r border-slate-200 last:border-r-0 h-full">
                    {/* Hourly click zones */}
                    {Array.from({ length: endHour - startHour + 1 }).map((_, i) => {
                      const hr = startHour + i;
                      return (
                        <div
                          key={hr}
                          onClick={() => handleGridCellClick(currentDate, hr)}
                          className={canEditEvents ? 'hover:bg-slate-50/40 transition-colors cursor-pointer' : ''}
                          style={{ height: `${hourHeight}px` }}
                        />
                      );
                    })}

                    {/* Render positioned event cards for this day */}
                    {layoutEventsForDay(currentDate).map(({ event, left, width }) => {
                      const cat = categories.find(c => c.id === event.calendarId);
                      const startMin = timeToMinutes(event.startTime);
                      const endMin = timeToMinutes(event.endTime);

                      // Convert to offset from 7 AM
                      const topOffset = ((startMin - startHour * 60) / 60) * hourHeight;
                      const cardHeight = ((endMin - startMin) / 60) * hourHeight;

                      if (topOffset < 0) return null; // out of scope bounds

                      return (
                        <div
                          key={event.id}
                          onClick={(e) => { e.stopPropagation(); if (onSelectEvent) onSelectEvent(event); else setSelectedEvent(event); }}
                          className="absolute rounded-xl shadow-md p-2.5 overflow-hidden border border-black/5 flex flex-col justify-between cursor-pointer hover:scale-[1.01] transition-transform select-none text-white text-[11px]"
                          style={{
                            top: `${topOffset}px`,
                            height: `${cardHeight}px`,
                            left: `${left}%`,
                            width: `${width}%`,
                            backgroundColor: cat?.color || '#3B82F6',
                          }}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center justify-between text-[11px] opacity-90 font-mono mb-1 font-bold">
                              <span>
                                {new Date(event.startTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                              </span>
                              {event.recurrenceRule && <Repeat className="h-2.5 w-2.5 shrink-0" />}
                            </div>
                            <p className="font-extrabold break-words leading-snug text-xs">{event.title}</p>
                            {event.assignedTo && (
                              <p className="text-[10px] opacity-90 break-words mt-0.5 flex items-center gap-1">
                                <User className="h-3 w-3 shrink-0" /> {event.assignedTo}
                              </p>
                            )}
                          </div>
                          {event.contactInfo && (
                            <p className="text-[11px] opacity-85 break-words font-mono mt-auto pt-1 flex items-center gap-1">
                              <Phone className="h-2.5 w-2.5" /> {event.contactInfo}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* 7 Week Day Columns */
                  weekDates.map((date, dayIdx) => {
                    const isToday = date.toDateString() === new Date().toDateString();
                    return (
                      <div key={dayIdx} className={`relative border-r border-slate-200 last:border-r-0 h-full ${isToday ? 'bg-blue-50/10' : ''}`}>
                        {/* Hourly Click Zones */}
                        {Array.from({ length: endHour - startHour + 1 }).map((_, i) => {
                          const hr = startHour + i;
                          return (
                            <div
                              key={hr}
                              onClick={() => handleGridCellClick(date, hr)}
                              className={canEditEvents ? 'hover:bg-slate-50/40 transition-colors cursor-pointer' : ''}
                              style={{ height: `${hourHeight}px` }}
                            />
                          );
                        })}

                        {/* Positioned Event Cards */}
                        {layoutEventsForDay(date).map(({ event, left, width }) => {
                          const cat = categories.find(c => c.id === event.calendarId);
                          const startMin = timeToMinutes(event.startTime);
                          const endMin = timeToMinutes(event.endTime);

                          const topOffset = ((startMin - startHour * 60) / 60) * hourHeight;
                          const cardHeight = ((endMin - startMin) / 60) * hourHeight;

                          if (topOffset < 0) return null;

                          return (
                            <div
                              key={event.id}
                              onClick={(e) => { e.stopPropagation(); if (onSelectEvent) onSelectEvent(event); else setSelectedEvent(event); }}
                              className="absolute rounded-xl shadow p-2 overflow-hidden border border-black/5 flex flex-col justify-between cursor-pointer hover:scale-[1.01] transition-transform select-none text-white text-[10px]"
                              style={{
                                top: `${topOffset}px`,
                                height: `${cardHeight}px`,
                                left: `${left}%`,
                                width: `${width}%`,
                                backgroundColor: cat?.color || '#3B82F6',
                              }}
                            >
                              <div className="min-w-0">
                                <div className="flex items-center justify-between text-[11px] opacity-90 font-mono mb-0.5">
                                  <span>
                                    {new Date(event.startTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                  {event.recurrenceRule && <Repeat className="h-2 w-2 shrink-0" />}
                                </div>
                                <p className="font-extrabold truncate leading-tight">{event.title}</p>
                                {event.assignedTo && (
                                  <p className="text-[11px] opacity-90 truncate flex items-center gap-0.5 mt-0.5">
                                    <User className="h-2.5 w-2.5 shrink-0" /> {event.assignedTo}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}

              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- MODAL OVERLAYS --- */}

      {/* Day / Date popup list modal (for days with multiple events) */}
      {datePopupDate && datePopupEvents.length > 0 && (
        <Modal onClose={() => { setDatePopupDate(null); setDatePopupEvents([]); }} ariaLabel="Events on Date">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <h3 className="text-xs font-bold text-slate-700">
                Events on {datePopupDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </h3>
              <button
                type="button"
                onClick={() => { setDatePopupDate(null); setDatePopupEvents([]); }}
                className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {datePopupEvents.map(ev => {
                const cat = categories.find(c => c.id === ev.calendarId);
                return (
                  <div
                    key={ev.id}
                    onClick={() => {
                      if (onSelectEvent) onSelectEvent(ev);
                      else setSelectedEvent(ev);
                      setDatePopupDate(null);
                      setDatePopupEvents([]);
                    }}
                    className="p-3 bg-slate-50 hover:bg-slate-100 border border-slate-200/60 rounded-xl cursor-pointer transition-all flex items-center justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-800 truncate">{ev.title}</p>
                      <p className="text-[10px] text-slate-500">
                        {new Date(ev.startTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                    <span
                      className="px-2 py-0.5 text-[11px] font-bold rounded-full border border-black/5"
                      style={{ backgroundColor: `${cat?.color}20`, color: cat?.color }}
                    >
                      {cat?.name || 'Event'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      )}

      {/* A. EVENT DETAIL MODAL */}
      {selectedEvent && (
        <Modal onClose={() => setSelectedEvent(null)} ariaLabel="Scheduled Event Details">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 font-sans animate-fade-in text-slate-800">
            <div className="flex items-start justify-between border-b border-slate-100 pb-3">
              <div>
                <span
                  className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-full border border-black/5"
                  style={{ backgroundColor: `${categories.find(c => c.id === selectedEvent.calendarId)?.color}20`, color: categories.find(c => c.id === selectedEvent.calendarId)?.color }}
                >
                  {categories.find(c => c.id === selectedEvent.calendarId)?.name || 'Event'}
                </span>
                <h3 className="text-base font-black text-slate-900 mt-1">{selectedEvent.title}</h3>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 cursor-pointer">
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div className="flex items-start gap-2.5">
                <Clock className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-slate-700">Date &amp; Scheduled Time</p>
                  <p className="text-slate-500 font-medium">
                    {new Date(selectedEvent.startTime).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {' – '}
                    {new Date(selectedEvent.endTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              {selectedEvent.assignedTo && (
                <div className="flex items-start gap-2.5">
                  <User className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-slate-700">Assigned Person</p>
                    <p className="text-slate-500 font-semibold">{selectedEvent.assignedTo}</p>
                  </div>
                </div>
              )}

              {selectedEvent.contactInfo && (
                <div className="flex items-start gap-2.5">
                  <Phone className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-slate-700">Contact Reference</p>
                    <p className="text-slate-500 font-mono font-bold">{selectedEvent.contactInfo}</p>
                  </div>
                </div>
              )}

              {selectedEvent.location && (
                <div className="flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-slate-700">Location / Venue</p>
                    <p className="text-slate-500 font-medium">{selectedEvent.location}</p>
                  </div>
                </div>
              )}

              {selectedEvent.recurrenceRule && (
                <div className="flex items-start gap-2.5">
                  <Repeat className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-slate-700">Recurrence Interval</p>
                    <p className="text-slate-500 font-bold text-brand">{selectedEvent.recurrenceRule} cycle</p>
                  </div>
                </div>
              )}

              {selectedEvent.description && (
                <div className="border-t border-slate-100 pt-3 flex flex-col gap-1">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Internal Notes</span>
                  <p className="text-slate-600 leading-relaxed font-medium whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>
              )}
            </div>

            {/* Admin actions inside event popup */}
            {userRole === 'admin' && (
              <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3.5 mt-4">
                <button
                  onClick={() => handleDeleteEvent(selectedEvent.id)}
                  className="px-3 py-1.5 border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl font-bold flex items-center gap-1 cursor-pointer"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
                <button
                  onClick={() => { setEditingEvent(selectedEvent); setIsCreating(false); setSelectedEvent(null); }}
                  className="px-4 py-1.5 bg-brand hover:bg-blue-600 text-white font-bold rounded-xl flex items-center gap-1 cursor-pointer shadow-md shadow-blue-500/10"
                >
                  <Settings className="h-3.5 w-3.5" /> Reschedule &amp; Edit
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* B. EVENT CREATE / EDIT MODAL FORM */}
      {editingEvent && (
        <Modal onClose={() => { setEditingEvent(null); setIsCreating(false); }} ariaLabel={isCreating ? "Add Schedule Item" : "Modify Schedule Item"}>
          <form onSubmit={handleSaveEvent} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 font-sans text-slate-800">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-brand" />
                {isCreating ? 'Schedule New Appointment' : 'Edit Appointment Details'}
              </h3>
              <button type="button" onClick={() => { setEditingEvent(null); setIsCreating(false); }} className="p-1 hover:bg-slate-100 rounded text-slate-400 cursor-pointer">
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="col-span-2 space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Event Title *</label>
                <input
                  type="text"
                  required
                  value={editingEvent.title}
                  onChange={e => setEditingEvent({ ...editingEvent, title: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-medium text-slate-800"
                />
              </div>

              <div className="space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Start Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={editingEvent.startTime.slice(0, 16)}
                  onChange={e => setEditingEvent({ ...editingEvent, startTime: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
                />
              </div>

              <div className="space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">End Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={editingEvent.endTime.slice(0, 16)}
                  onChange={e => setEditingEvent({ ...editingEvent, endTime: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
                />
              </div>

              <div className="space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Calendar Layer *</label>
                <select
                  value={editingEvent.calendarId}
                  onChange={e => setEditingEvent({ ...editingEvent, calendarId: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-bold text-slate-700 cursor-pointer"
                >
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>

              <div className="space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Assigned To</label>
                <SearchableSelect
                  value={editingEvent.assignedTo || ''}
                  onChange={v => setEditingEvent({ ...editingEvent, assignedTo: v })}
                  options={agents.map(agent => ({
                    value: agent.name,
                    label: `${agent.name} (${agent.email})${agent.role ? ` · ${agent.role}` : ''}`,
                  }))}
                  placeholder="Search agents…"
                  clearLabel="-- Not assigned --"
                  emptyLabel={agentsError ? 'Failed to load agents — refresh' : !agentsLoaded ? 'Loading workspace agents...' : 'No agents in this workspace'}
                />
              </div>

              <div className="space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Contact Phone</label>
                <input
                  type="text"
                  placeholder="+1 (555) 000-0000"
                  value={editingEvent.contactInfo || ''}
                  onChange={e => setEditingEvent({ ...editingEvent, contactInfo: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-mono font-semibold"
                />
              </div>

              <div className="space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Location / Link</label>
                <input
                  type="text"
                  placeholder="OR-3, Suite B, Zoom Link"
                  value={editingEvent.location || ''}
                  onChange={e => setEditingEvent({ ...editingEvent, location: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-medium"
                />
              </div>

              <div className="col-span-2 space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Recurrence</label>
                <select
                  value={editingEvent.recurrenceRule || ''}
                  onChange={e => setEditingEvent({ ...editingEvent, recurrenceRule: e.target.value || undefined })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-semibold text-slate-700 cursor-pointer"
                >
                  <option value="">No Recurrence</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Bi-Weekly">Bi-Weekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
              </div>

              <div className="col-span-2 space-y-1">
                <label className="font-extrabold text-slate-700 uppercase tracking-wide">Description &amp; Notes</label>
                <textarea
                  rows={3}
                  placeholder="Add details, prep instructions..."
                  value={editingEvent.description || ''}
                  onChange={e => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:bg-white focus:border-brand font-medium resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => { setEditingEvent(null); setIsCreating(false); }}
                className="px-3 py-2 border border-slate-200 hover:bg-slate-50 rounded-xl font-bold transition"
              >
                Dismiss
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-brand hover:bg-blue-600 text-white font-bold rounded-xl transition shadow-md shadow-blue-500/10"
              >
                {isCreating ? 'Create Appointment' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}

    </div>
  );
}